import * as THREE from 'three';
import { HollyRig } from '@/anim/hollyRig';
import { ease } from '@/anim/types';
import { HOLLY_BELL_STRIDE_METRES, bellEmissionRadius } from '@/audio/sounds/bell';
import { HollyPresenter, drainHollyBellQueue } from '@/game/hollyPresenter';
import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition, RoomTick } from '@/game/roomModule';
import { getZone } from '@/render/palette';
import type { RoomDiagnostics } from '@/game/roomDiagnostics';
import { HOLLY_BELL_HEIGHT, hollyAsset } from '@/models/holly';
import { CompanionBell, type CompanionBellEvent } from '@/systems/companionBell';
import { HOLLY_RESTING, WOOL_SHED_REST_FLAGS } from '@/story/act2/woolShedRest';
import { HOLLY_TRAPPED, WOOL_SHED_IDS, WOOL_SHED_PUZZLE_FLAGS, WoolShedPuzzle } from '@/story/act2/woolShedPuzzle';
import type { HallSolid } from '@/world/solids';
import {
  createWoolShed, createWoolShedProps, DROP_BAR, woolShedCompanionObstacles, woolShedSolids,
  WOOL_SHED_DOOR_POSES, WOOL_SHED_DROP_BAR_POSES, WOOL_SHED_EXIT_Z, WOOL_SHED_PALLET_POSES,
  WOOL_SHED_PALLET_ROOT, WOOL_SHED_WIRE_POSES, type WoolShedPropPose,
  WOOL_SHED_SPAWN, WOOL_SHED_ZONE_ID, woolShedAmbience, woolShedLighting, woolShedNightSky, type WoolShedPart,
} from '@/world/woolShed';

/**
 * Beat 9's shed, and it is four things that must not be allowed to disagree.
 *
 * `puzzle` is the truth: a pure fixed-step machine that knows whether the
 * pallet is off the wire and whether the bar is down. `props` is the picture,
 * `colliders` is what she walks into, and `rig` is the animal. All three are
 * DOWNSTREAM of the puzzle and none of them writes back to it — the same rule
 * the camera and the audio keep, and here it is what stops a finished animation
 * from opening a door.
 *
 * The collider handles are held because the swap is the half of the state
 * change only this room module can do (the hedgerow's boards say the same thing
 * for the same reason). The prop poses are presentation only: each eases from
 * its old authored pose to its new one on the puzzle's own settle clock
 * (`palletProgress`, `doorProgress`, D-157), so the pallet visibly falls rather
 * than teleporting, and the colliders move on the fixed ticks those clocks start
 * and finish on. (This said `woolShedBlend`, a render-side ease that D-157
 * replaced; the claim outlived it by a refactor.)
 */
type Colliders = Record<'palletPinned' | 'palletTipped' | 'dropBar' | 'doorLeaf' | 'doorLeafOpen', ColliderHandle | null>;

function required(root: THREE.Object3D, name: string, part: number): THREE.Object3D {
  const node = root.getObjectByName(name);
  if (node === undefined) {
    throw new Error(`wool-shed part ${String(part)}: missing "${name}"`);
  }
  return node;
}

function requiredSolid(solids: readonly HallSolid[], part: 1 | 2, name: string): HallSolid {
  const solid = solids.find((entry) => entry.name === name);
  if (solid === undefined) {
    throw new Error(`wool-shed part ${String(part)}: missing "${name}"`);
  }
  return solid;
}

function pose(node: THREE.Object3D, from: WoolShedPropPose, to: WoolShedPropPose, t: number): void {
  node.position.set(
    from.position.x + (to.position.x - from.position.x) * t,
    from.position.y + (to.position.y - from.position.y) * t,
    from.position.z + (to.position.z - from.position.z) * t,
  );
  node.rotation.set(
    from.rotation.x + (to.rotation.x - from.rotation.x) * t,
    from.rotation.y + (to.rotation.y - from.rotation.y) * t,
    from.rotation.z + (to.rotation.z - from.rotation.z) * t,
  );
}

/**
 * The two completion sets beat 9's puzzle is ever handed.
 *
 * Allocated once because the room module's fixed step runs 60 times a second
 * and the puzzle takes a `ReadonlySet`; building a fresh one per tick would be
 * the only garbage this loop produces.
 */
const EMPTY_COMPLETIONS: ReadonlySet<string> = new Set<string>();
const COMPLETED_PALLET: ReadonlySet<string> = new Set([WOOL_SHED_IDS.pallet]);

export function createWoolShedRoomModule(
  options: { part: number; dressing: boolean; out?: number },
  capabilities: RoomCapabilities,
  createProps: () => THREE.Group = createWoolShedProps,
): RoomModule {
  const part: 1 | 2 = options.part === 1 ? 1 : 2;
  const authored = { part, dressing: options.dressing } as const;
  const shed = createWoolShed(authored);
  capabilities.scene.add(shed);
  const colliders: Colliders = {
    palletPinned: null,
    palletTipped: null,
    dropBar: null,
    doorLeaf: null,
    doorLeafOpen: null,
  };
  const solids = woolShedSolids(authored);
  const tippedPalletSolid = part === 1 ? requiredSolid(solids, 1, 'pallet-tipped') : null;
  const openLeafSolid = part === 1 ? requiredSolid(woolShedSolids({ part: 2, dressing: options.dressing }), 2, 'door-leaf-open') : null;
  for (const solid of solids) {
    // **In part 1 only.** The swung-open leaf's collider arrives when the bar
    // goes; before that the shut leaf is what is there.
    //
    // Part 2 keeps it, and that is a correction. It used to be skipped in both
    // parts under a comment reading *"deliberately drawable but not physical:
    // rest state must prove the same doorway input that stops in part 1 now
    // exits it"* — which confused two different things. The leaf parked
    // against the wall at x 1.10 … 1.22 is nowhere near the 2.2 m opening, so
    // giving it collision does not make the doorway one bit less passable; all
    // its absence bought was a visible slab a player could walk through. An
    // independent review found the two parts disagreeing about it after part 1
    // learned to open its own door.
    // **The tipped pallet's collider is not created at boot.** Both poses are
    // in the solids list so the shapes come from one place, but only one of
    // them is real at a time, and the pinned one is the one the puzzle starts
    // in. Creating both would leave a pallet-shaped wall leaning on the bales
    // from the first frame, which is invisible, and reachable, and would have
    // read as the character controller catching on nothing.
    if ((solid.name === 'door-leaf-open' && part === 1) || solid.name === 'pallet-tipped') {
      continue;
    }
    const handle = capabilities.colliders.add(solid);
    if (solid.name === 'pallet-pinned') {
      colliders.palletPinned = handle;
    } else if (solid.name === 'drop-bar') {
      colliders.dropBar = handle;
    } else if (solid.name === 'door-leaf') {
      colliders.doorLeaf = handle;
    } else if (solid.name === 'door-leaf-open') {
      colliders.doorLeafOpen = handle;
    }
  }
  const interactables = part === 1
    ? [
      // The pallet's south-west corner, under `root`. `duration` is what makes it
      // a heave rather than a bump — the same argument beat 5's bin makes, and
      // the reason a pig's verb for "shift something heavy" is this one.
      {
        id: WOOL_SHED_IDS.pallet,
        at: { ...WOOL_SHED_PALLET_ROOT },
        verbs: ['root'] as const,
        reach: 0.85,
        duration: 2.2,
      },
    ]
    : [];
  const resting = part === 2;
  const props = resting ? null : createProps();
  if (props !== null) capabilities.scene.add(props);
  const pallet = props === null ? null : required(props, 'wool-shed-pallet', part);
  const wire = props === null ? null : required(props, 'wool-shed-wire', part);
  const bar = props === null ? null : required(props, 'wool-shed-drop-bar', part);
  // The door leaf's parent is the room's DRESSING group and not the props
  // group — the leaf is built with the shed because part 2 needs it too, and it
  // became posable only when part 1 learned to open it. With `dress=0` there is
  // no leaf to pose, which is why this one handle is optional.
  const leaf = !resting && options.dressing ? required(shed, 'wool-shed-door-leaf', part) : null;
  // Holly, in the room for the first time. She is placed from the puzzle's
  // own authored start rather than from a second copy of those numbers, so
  // there is exactly one answer to where she is.
  const root = hollyAsset.create();
  const initial = resting ? HOLLY_RESTING : HOLLY_TRAPPED;
  root.position.set(initial.x, 0, initial.z);
  root.rotation.y = initial.yaw;
  capabilities.scene.add(root);
  const rig = new HollyRig(root);
  const presenter = new HollyPresenter(root, rig);
  let disposed = false;
  if (resting) {
    return {
      interactables,
      fixedStep(tick): void {
        // ── Leaving the shed, which is the only thing that ends either part ────
        //
        // One threshold, `WOOL_SHED_EXIT_Z`, used by both parts on purpose: that
        // is what makes "part 1 stops short of it" and "part 2 carries through it"
        // a delta rather than two unrelated numbers (`wool-shed.spec.ts`).
        //
        // And part 2 requires only the crossing, because the rest state has no
        // other condition of any kind. That is the content, not an omission.
        if (tick.player.z >= WOOL_SHED_EXIT_Z) {
          tick.raiseFlag(WOOL_SHED_REST_FLAGS.leftTheShed);
        }
      },
      renderStep(frameDt): void {
        // **Part 2's Holly is the same mesh and the same rig, and no simulation.**
        // She is placed from `HOLLY_RESTING`, an authored constant, and there is
        // nothing in this part that could move her afterwards — no `Companion`, no
        // puzzle, no station, no step. That is what makes the rest state's promise
        // structural rather than tested: see `woolShedRest.ts`.
        // **Holly runs from whichever snapshot the part has**, and there are two:
        // part 1's is the puzzle's live companion, part 2's is `HOLLY_RESTING`,
        // an authored constant with no controller behind it (see `woolShedRest.ts`
        // on why the rest state cannot have one). Every clip below reads it and
        // none writes it, which is the same boundary the props keep.
        presenter.update(frameDt, HOLLY_RESTING, null, null);
      },
      framing: () => null,
      diagnostics: (): RoomDiagnostics => ({
        kind: 'wool-shed',
        woolShed: null,
        holly: {
          x: HOLLY_RESTING.x,
          z: HOLLY_RESTING.z,
          yaw: HOLLY_RESTING.yaw,
          phase: HOLLY_RESTING.phase,
          moving: HOLLY_RESTING.moving,
          muzzleY: rig.muzzleWorldY(),
        },
      }),
      dispose(): void {
        if (disposed) {
          return;
        }
        disposed = true;
        capabilities.scene.remove(root);
        capabilities.scene.remove(shed);
      },
    };
  }
  const puzzle = new WoolShedPuzzle({ obstacles: woolShedCompanionObstacles(authored) });
  // The puzzle's own opening snapshot is the cadence origin, and part 2
  // deliberately has no live companion, so it also has no bell that could
  // ring at rest.
  //
  // **These two objects share one trace and nothing enforces it.**
  // `CompanionBell.advance()` rejects a pair that does not continue the one
  // it last accepted, so `WoolShedPuzzle.reset()` — which rebuilds the
  // companion at travelled 0 — would make the next tick throw. It has no
  // caller: a restart or a part change reloads and rebuilds both, and
  // `respawn()` moves only Rosalie. An independent review raised it, and it
  // is left as a loud failure rather than a silent resync because a bell
  // that quietly re-based its cadence would be indistinguishable from one
  // that had stopped ringing. **Whoever gives beat 10 a live reset resets
  // the bell in the same statement**; it is filed in `docs/backlog.md`.
  const bell = new CompanionBell({
    strideDistance: HOLLY_BELL_STRIDE_METRES,
    soundRadius: bellEmissionRadius(),
  }, puzzle.snapshot().holly);
  const queue: CompanionBellEvent[] = [];
  let events = 0;
  let last: { thresholdDistance: number; x: number; z: number; radius: number } | null = null;
  return {
    interactables,
    fixedStep(tick: RoomTick): void {
      // ── Beat 9's two gates, on the fixed step, upstream of everything ──────
      //
      // The puzzle is the only thing that decides whether the wire is off Holly
      // and whether the bar is down. It reads her position and the completed
      // interaction ids and nothing else; the props, the colliders and her rig
      // all read the snapshot it returns. That direction is the whole design
      // (`docs/beat-9-plan.md`'s companion contract), and it is what makes the
      // ordering testable in Node without a frame.
      const before = puzzle.snapshot();
      const after = puzzle.step(
        tick.dt,
        { x: tick.player.x, z: tick.player.z },
        tick.completed(WOOL_SHED_IDS.pallet) ? COMPLETED_PALLET : EMPTY_COMPLETIONS,
      );
      // ── Holly's bell ──────────────────────────────────────────────────
      //
      // The cadence is distance, never a clock: `CompanionBell` consumes the
      // puzzle's own before/after pair and returns the marks she crossed. It
      // must be fed EVERY tick the puzzle steps, including the still ones —
      // it validates that each pair continues the trace it last accepted, so
      // a skipped tick is an exception rather than a missed bell.
      //
      // **Stealth here, audio on the render step.** Sound emission is
      // simulation — a watcher's suspicion is game state and must be
      // identical with audio muted (D-045) — so `emitSound` happens on the
      // fixed step. Playing the sound is presentation, and doing it here made
      // the strokes of one catch-up burst start at the same audio time and hit
      // the engine's per-id voice cap, which drops the later ones. An
      // independent review found that; the queue below is the fix.
      for (const event of bell.advance(before.holly, after.holly)) {
        const position = { x: event.position.x, y: HOLLY_BELL_HEIGHT, z: event.position.z };
        events += 1;
        last = {
          thresholdDistance: event.thresholdDistance,
          x: event.position.x,
          z: event.position.z,
          radius: event.radius,
        };
        // The wool shed constructs no `StealthSystem` — the exterior worker
        // round is cut (D-153) — so this is inert in beat 9 and is the whole
        // of the wiring beat 10's watcher needs. It is not a dead branch by
        // accident. Nothing gates it today; `companion-bell.spec.ts` proves
        // the consequence with a real system, not this call.
        capabilities.emitSound({ position, radius: event.radius });
        queue.push(event);
      }

      // The collider swap is the half of the state change only this room module can
      // do, so it happens on the SAME fixed tick the state changed on — not on
      // the render step, where a dropped frame would leave her briefly walking
      // through a pallet that is still standing in the picture.
      //
      // **Each collider LEAVES on the edge and ARRIVES when its settle clock
      // finishes**, and the gap between the two is not sloppiness — it is the
      // second the thing spends in the air.
      //
      // The first build removed and created on the same tick and let the mesh
      // ease toward its new pose over render frames, so for about a second the
      // pallet's collision leaned against the bales while its picture lay flat
      // on the floor. The comment here claimed swapping on the fixed step was
      // what PREVENTED that; an independent review pointed out it was what
      // caused it. `palletProgress` and `doorProgress` are the one clock each
      // event gets (D-157), the mesh pose below reads the same number, and
      // `wool-shed-integration.spec.ts` asserts they agree at both ends.
      if (before.pallet !== after.pallet && colliders.palletPinned !== null) {
        capabilities.colliders.remove(colliders.palletPinned);
        colliders.palletPinned = null;
      }
      if (after.palletProgress >= 1 && colliders.palletTipped === null && tippedPalletSolid !== null) {
        colliders.palletTipped = capabilities.colliders.add(tippedPalletSolid);
      }
      if (!before.doorOpen && after.doorOpen) {
        // The leaf stops blocking the opening, and the bar's collider goes
        // with it: a 90 mm timber lying flat against a wall is not something a
        // 0.32 m pig is stopped by.
        if (colliders.doorLeaf !== null) {
          capabilities.colliders.remove(colliders.doorLeaf);
          colliders.doorLeaf = null;
        }
        if (colliders.dropBar !== null) {
          capabilities.colliders.remove(colliders.dropBar);
          colliders.dropBar = null;
        }
      }
      if (after.doorProgress >= 1 && colliders.doorLeafOpen === null && openLeafSolid !== null) {
        colliders.doorLeafOpen = capabilities.colliders.add(openLeafSolid);
      }
      // ── Leaving the shed, which is the only thing that ends either part ────
      //
      // One threshold, `WOOL_SHED_EXIT_Z`, used by both parts on purpose: that
      // is what makes "part 1 stops short of it" and "part 2 carries through it"
      // a delta rather than two unrelated numbers (`wool-shed.spec.ts`).
      //
      // Part 1 requires the door to be OPEN as well as crossed. The shut leaf
      // already stops her, so the condition costs nothing and it says what it
      // means — a beat that could end by some other route through this plane
      // would end without its puzzle.
      if (after.doorOpen && tick.player.z >= WOOL_SHED_EXIT_Z) {
        tick.raiseFlag(WOOL_SHED_PUZZLE_FLAGS.throughTheDoor);
      }
    },
    renderStep(frameDt): void {
      // ── Beat 9's shed, on the render step, reading and never writing ───────
      //
      // Everything below is PRESENTATION. The puzzle has already decided, on the
      // fixed step, whether the pallet is off and whether the bar is down; these
      // lines only catch the picture up to it. Nothing here can advance a gate,
      // which is what stops a finished animation from opening a door — the
      // failure mode `woolShedPuzzle.ts` exists to make impossible.
      const state = puzzle.snapshot();
      // **The poses come from the puzzle's own settle clocks, not from a
      // render-side ease.** They used to be exponential blends running on
      // `frameDt`, which meant the picture arrived whenever it arrived while
      // the colliders had already moved — see the note in the fixed step. One
      // number per event (D-157), read here and read there.
      //
      // `ease.inOut` on top so each prop starts and stops without a jerk. It
      // is a curve over the same 0..1, not a second clock: it cannot change
      // WHEN the prop arrives, only how it travels.
      const fall = ease.inOut(state.palletProgress);
      if (pallet !== null && wire !== null && bar !== null) {
        pose(pallet, WOOL_SHED_PALLET_POSES.pinned, WOOL_SHED_PALLET_POSES.tipped, fall);
        // The wire slumps as the weight comes off it, so it is on the pallet's
        // clock and slightly ahead of it — it is springing, not falling.
        pose(wire, WOOL_SHED_WIRE_POSES.pinned, WOOL_SHED_WIRE_POSES.slack, Math.min(1, fall * 1.35));
        // The bar drops out of its brackets long before the leaf finishes
        // swinging, which is the order these two things actually happen in.
        pose(bar, WOOL_SHED_DROP_BAR_POSES.down, WOOL_SHED_DROP_BAR_POSES.lifted, Math.min(1, ease.in(state.doorProgress) * 2.6));
      }
      if (leaf !== null) {
        pose(leaf, WOOL_SHED_DOOR_POSES.shut, WOOL_SHED_DOOR_POSES.open, ease.inOut(state.doorProgress));
      }

      // **The bell is played here, on the render step, from what simulation
      // decided.** The events carry their own world position, so the sound is
      // where she was when she crossed the mark rather than where she is now —
      // and presentation cannot move the cadence, only voice it (D-045).
      //
      // The queue is drained whole every frame. A frame that ran several fixed
      // ticks plays several strokes, which is correct: they happened.
      if (queue.length > 0) {
        drainHollyBellQueue(queue, capabilities.playOneShot);
      }
      // **Holly runs from whichever snapshot the part has**, and there are two:
      // part 1's is the puzzle's live companion, part 2's is `HOLLY_RESTING`,
      // an authored constant with no controller behind it (see `woolShedRest.ts`
      // on why the rest state cannot have one). Every clip below reads it and
      // none writes it, which is the same boundary the props keep.
      presenter.update(frameDt, state.holly, state, null);
    },
    framing: () => null,
    diagnostics: (): RoomDiagnostics => {
      const state = puzzle.snapshot();
      return {
        kind: 'wool-shed',
        woolShed: {
          pallet: state.pallet,
          wire: state.wire,
          bar: state.bar,
          doorOpen: state.doorOpen,
          latchProgress: state.latchProgress,
          riseProgress: state.riseProgress,
          holly: {
            x: state.holly.x,
            z: state.holly.z,
            yaw: state.holly.yaw,
            phase: state.holly.phase,
            moving: state.holly.moving,
            travelled: state.holly.travelledDistance,
          },
          barY: bar?.position.y ?? Number.NaN,
          leafYaw: leaf?.rotation.y ?? Number.NaN,
          openLeafSolid: colliders.doorLeafOpen !== null,
          tippedPalletSolid: colliders.palletTipped !== null,
          palletProgress: state.palletProgress,
          doorProgress: state.doorProgress,
          muzzleY: rig.muzzleWorldY(),
          barHeight: DROP_BAR.height,
          bell: last === null ? null : {
            events,
            lastThresholdDistance: last.thresholdDistance,
            lastX: last.x,
            lastZ: last.z,
            radius: last.radius,
          },
        },
        holly: {
          x: state.holly.x,
          z: state.holly.z,
          yaw: state.holly.yaw,
          phase: state.holly.phase,
          moving: state.holly.moving,
          muzzleY: rig.muzzleWorldY(),
        },
      };
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      if (props !== null) {
        capabilities.scene.remove(props);
      }
      capabilities.scene.remove(root);
      capabilities.scene.remove(shed);
    },
  };
}
/**
 * Part selection constructs a complete world state. There is no live swap from
 * a shut leaf into an open one: the URL/save boundary owns which state exists,
 * so presentation and Rapier begin from the same authored list. Part 1 keeps
 * the zone's daylight background — a doorway onto an afternoon is meant to
 * blow out. Part 2 does not; see `woolShedNightSky`.
 */
function woolShedEnvironment(options: { part: number }): RoomEnvironment {
  const zone = getZone(WOOL_SHED_ZONE_ID);
  if (!zone) throw new Error(`unknown zone "${WOOL_SHED_ZONE_ID}"`);
  const part: WoolShedPart = options.part === 1 ? 1 : 2;
  return {
    zone,
    lighting: woolShedLighting(part),
    background: part === 1 ? { kind: 'rig' } : woolShedNightSky(),
    spawn: WOOL_SHED_SPAWN,
    ambience: woolShedAmbience(),
  };
}

export const WOOL_SHED_ROOM_MODULE: RoomModuleDefinition = {
  room: 'wool-shed',
  environment: woolShedEnvironment,
  create: createWoolShedRoomModule,
};
